import { useState, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy, addDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useUserAuth } from '../Context/UserAuthContext';

// Hook to fetch posts for current user
export const useUserPosts = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useUserAuth();

  const fetchUserPosts = async () => {
    if (!user) {
      setPosts([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const postsRef = collection(db, 'posts');
      // Only filter by userId - sorting done client-side to avoid index requirement
      const q = query(postsRef, where('userId', '==', user.uid));
      const querySnapshot = await getDocs(q);
      const postsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by createdAt on client side
      postsData.sort((a, b) => {
        const timeA = a.createdAt?.toMillis() || 0;
        const timeB = b.createdAt?.toMillis() || 0;
        return timeB - timeA; // Newest first
      });
      
      setPosts(postsData);
      setError(null);
    } catch (err) {
      console.error('Error fetching user posts:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserPosts();
  }, [user]);

  return { posts, loading, error, refetch: fetchUserPosts };
};

// Hook to fetch all posts from all users
export const useAllPosts = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAllPosts = async () => {
    try {
      setLoading(true);
      const postsRef = collection(db, 'posts');
      // Get all posts without orderBy to avoid index requirement
      const querySnapshot = await getDocs(postsRef);
      const postsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by createdAt on client side
      postsData.sort((a, b) => {
        const timeA = a.createdAt?.toMillis() || 0;
        const timeB = b.createdAt?.toMillis() || 0;
        return timeB - timeA; // Newest first
      });
      
      setPosts(postsData);
      setError(null);
    } catch (err) {
      console.error('Error fetching all posts:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllPosts();
  }, []);

  return { posts, loading, error, refetch: fetchAllPosts };
};

// Function to create a new post
export const createPost = async (userId, userName, title, content) => {
  try {
    const postsRef = collection(db, 'posts');
    const newPost = await addDoc(postsRef, {
      userId,
      userName,
      title,
      content,
      createdAt: serverTimestamp(),
    });
    return { success: true, id: newPost.id };
  } catch (err) {
    console.error('Error creating post:', err);
    return { success: false, error: err.message };
  }
};

// Function to delete a post
export const deletePost = async (postId) => {
  try {
    await deleteDoc(doc(db, 'posts', postId));
    return { success: true };
  } catch (err) {
    console.error('Error deleting post:', err);
    return { success: false, error: err.message };
  }
};

